import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { MessageCircle, Clock } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import Navigation from '../components/Navigation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getDefaultPhoto = (name) => `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'G')}&background=0F3B2E&color=fff&size=128&font-size=0.4&bold=true`;
const getPhotoUrl = (url) => url?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
const formatTime = (dateStr) => {
  if (!dateStr) return '';
  try { return formatDistanceToNow(new Date(dateStr), { addSuffix: true }); } catch { return ''; }
};

const MessagesList = () => {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMatches = async () => {
      try {
        const res = await axios.get(`${API}/matches`);
        setMatches(res.data);
      } catch { /* silent */ } finally { setLoading(false); }
    };
    fetchMatches();
  }, []);

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="messages-list-page">
      <div className="px-4 py-3 sticky top-0 z-20 border-b border-border bg-background/90 backdrop-blur-xl">
        <h1 className="font-serif text-2xl font-bold text-foreground">Messages</h1>
        <p className="text-xs text-muted-foreground">{matches.length} conversations</p>
      </div>

      <div className="max-w-lg mx-auto px-4 pt-2">
        {loading ? (
          <div className="space-y-3 pt-2">
            {[1, 2, 3].map(i => (
              <div key={i} className="animate-pulse flex items-center gap-4 p-4 rounded-2xl bg-muted">
                <div className="w-14 h-14 rounded-full bg-muted-foreground/20" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 rounded w-1/2 bg-muted-foreground/20" />
                  <div className="h-3 rounded w-3/4 bg-muted-foreground/20" />
                </div>
              </div>
            ))}
          </div>
        ) : matches.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center bg-muted">
              <MessageCircle className="w-10 h-10 text-muted-foreground" />
            </div>
            <h2 className="font-serif text-lg font-semibold mb-2 text-foreground">No Messages Yet</h2>
            <p className="text-sm text-muted-foreground">Match with golfers to start chatting!</p>
          </div>
        ) : (
          <div className="space-y-2 pt-2">
            {matches.map(match => (
              <div
                key={match.id}
                className="p-3 rounded-2xl border border-border cursor-pointer transition-all bg-card hover:border-primary/30"
                onClick={() => navigate(`/messages/${match.id}`)}
                data-testid={`match-card-${match.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Avatar className="w-14 h-14 border-2 border-border">
                      <AvatarImage src={getPhotoUrl(match.other_user?.profile_photo) || getDefaultPhoto(match.other_user?.display_name)} />
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        {match.other_user?.display_name?.charAt(0) || 'G'}
                      </AvatarFallback>
                    </Avatar>
                    {match.unread_count > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center px-1">
                        {match.unread_count}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-0.5">
                      <h3 className="font-semibold text-sm truncate text-foreground">
                        {match.other_user?.display_name}
                      </h3>
                      {(match.last_message || match.created_at) && (
                        <span className="text-[10px] flex items-center gap-0.5 text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          {formatTime(match.last_message?.created_at || match.created_at)}
                        </span>
                      )}
                    </div>
                    {match.last_message ? (
                      <p className="text-xs truncate text-muted-foreground">{match.last_message.content}</p>
                    ) : (
                      <p className="text-xs text-primary">Start a conversation</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Navigation />
    </div>
  );
};

export default MessagesList;
